import { ApiError, fetcher } from "@/lib/api";
import { createRefreshCoordinator } from "./refresh-coordinator";

export type MeEntitlements = {
  resolutionTier: "internal_unlimited" | "subscription";
  features: {
    quizPlatform: { unlimited: boolean; plan?: string };
    ide: { plan: "free" | "pro" | "internal" };
    aiCredits: { unlimited: boolean; remaining?: number };
    courseProgress: { enrollmentRequired: boolean };
  };
};

export type AuthUser = {
  _id: string;
  name: string;
  email?: string;
  accountKind?: "full" | "contest_temp";
  /** `EX-DAY04`: `warmUp` is the account's contest's scheduled warm-up, when it has one. */
  tempContest?: {
    slug: string;
    expiresAt: string;
    endAt: string;
    warmUp?: { slug: string; startAt: string | null; endAt: string | null } | null;
  };
  role: string;
  language: string;
  permissions: string[];
  /** Present on GET /v1/me when using an impersonation access token. */
  impersonation?: {
    actorUserId: string;
    actorName: string;
    subjectUserId: string;
    subjectName: string;
  };
  /**
   * The site's proof of `_id` for the analytics tracker (`AN-INT01`): an HMAC
   * the API issues from the Chokh site's `identifySecret`. `ps_web` runs with
   * `allowUnsignedIdentify` off, so an identify without it is refused and the
   * visitor stays anonymous. The auth payload starts carrying it in
   * `AN-INT02`; until then it is absent and the identify call passes
   * `undefined`.
   */
  analyticsSignature?: string;
  /** Confirmed ownership of `email`. Soft gate — unverified accounts still work. */
  emailVerified?: boolean;
  /** Platform internal operator — unlimited entitlements (not RBAC). */
  isInternal?: boolean;
  entitlements?: MeEntitlements;
  // Profile fields
  bio?: string;
  phone?: string;
  institution?: string;
  jobTitle?: string;
  address?: string;
  city?: string;
  country?: string;
  /** `EX-IDN03`: ISO 3166-1 alpha-2, the flag; `null` or absent when not given. */
  countryCode?: string | null;
  /** `EX-IDN02`: the handle in the person's capitals; `usernameChosen` false is the email-made one. */
  usernameDisplay?: string;
  usernameChosen?: boolean;
  /**
   * `EX-IDN05` (ADR-0088): what a contest registration still needs (a chosen
   * username, a country, an institution), and whether the Welcome screen is owed.
   */
  contestProfile?: { complete: boolean; missing: Array<"username" | "country" | "institution"> };
  welcomePending?: boolean;
  githubUsername?: string;
  codeforcesHandle?: string;
  linkedinUrl?: string;
  facebookUrl?: string;
  websiteUrl?: string;
  avatarImageId?: string;
  /** Unique student identifier, e.g. "PRG-2026-00042". Null for legacy/non-student accounts. */
  studentId?: string;
  /** Public referral code, e.g. STU-PRG-2026-00042 */
  referralCode?: string;
  /** Canonical public profile slug (assigned server-side). */
  username?: string;
  /** Course credit balance (BDT) from student referrals */
  referralCredit?: number;
  createdAt?: string;
  /** Campus ambassador (subset for client UI; full hub loads from /v1/me/ambassador). */
  ambassadorProfile?: {
    isAmbassador: boolean;
    status?: "active" | "restricted" | "terminated";
    referralCode?: string;
  };
  /** `GR-PRT01`: present when the account is a partner; the page loads from /v1/me/partner. */
  partnerProfile?: { status: "active" | "paused" };
};

type LoginResponse = { accessToken: string; user: AuthUser };
type RegisterResponse = { accessToken: string; user: AuthUser };
/**
 * `user` is the account `GET /v1/me` would answer, sent with the token since
 * `AD-DSH02`; an API without the flag leaves it out and the caller reads
 * `/v1/me` instead.
 */
export type RefreshResponse = { accessToken: string; user?: AuthUser };

/**
 * `roomCode` (`EX-VEN01`): the six digits on the hall's screen, sent only when
 * an issued login's contest asked for them (`403 ROOM_CODE_REQUIRED`).
 */
export async function authLogin(email: string, password: string, roomCode?: string): Promise<LoginResponse> {
  const identity = email.includes("@") ? { email, password } : { username: email, password };
  const res = await fetcher<LoginResponse>("/v1/auth/login", {
    method: "POST",
    body: JSON.stringify(roomCode ? { ...identity, roomCode } : identity),
  });
  if (!res.success) throw new Error(res.error?.message);
  return res.data;
}

/**
 * Asks the API to email a reset link. Resolves the same way for every address —
 * the endpoint deliberately does not reveal whether an account exists.
 */
export async function authPasswordResetRequest(email: string): Promise<void> {
  const res = await fetcher<{ ok: boolean }>("/v1/auth/password-reset/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  if (!res.success) throw new Error(res.error?.message);
}

export async function authPasswordResetComplete(
  token: string,
  newPassword: string,
  confirmPassword: string,
): Promise<LoginResponse> {
  const res = await fetcher<LoginResponse>("/v1/auth/password-reset/complete", {
    method: "POST",
    body: JSON.stringify({ token, newPassword, confirmPassword }),
  });
  if (!res.success) throw new Error(res.error?.message);
  return res.data;
}

export type EmailVerificationOutcome = "sent" | "already_verified" | "throttled";

/** Resends the confirmation link to the signed-in user's own address. */
export async function authEmailVerificationRequest(
  token: string,
): Promise<EmailVerificationOutcome> {
  const res = await fetcher<{ outcome: EmailVerificationOutcome }>(
    "/v1/auth/email-verification/request",
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({}),
    },
  );
  if (!res.success) throw new Error(res.error?.message);
  return res.data.outcome;
}

/** Redeems a verification link. Idempotent — a link opened twice still resolves. */
export async function authEmailVerificationConfirm(
  token: string,
): Promise<{ email: string; alreadyVerified: boolean }> {
  const res = await fetcher<{ email: string; alreadyVerified: boolean }>(
    "/v1/auth/email-verification/confirm",
    {
      method: "POST",
      body: JSON.stringify({ token }),
    },
  );
  if (!res.success) throw new Error(res.error?.message);
  return res.data;
}

export async function authRegister(
  name: string,
  email: string,
  password: string,
  phone?: string,
  institution?: string,
): Promise<RegisterResponse> {
  const payload: Record<string, string> = { name, email, password };
  if (phone) payload.phone = phone;
  // `EX-IDN01`: `institution`, the field the profile and the standings read. The
  // form sent `organization` before, which was stored where nothing reads it.
  if (institution) payload.institution = institution;
  const res = await fetcher<RegisterResponse>("/v1/auth/register", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.success) throw new Error(res.error?.message);
  return res.data;
}

/**
 * Every refresh asks for the account with the token (`AD-DSH02`), and sends no
 * body: a POST with no body and no custom header is a simple request, so the
 * session check a visit opens with (no access token yet) is not preflighted.
 */
export const REFRESH_PATH = "/v1/auth/refresh?includeUser=1";

/**
 * Where the session check the HTML parser starts leaves its answer, for
 * `authRefresh` to take once (`features/auth/early-session-check.ts`).
 */
export const EARLY_SESSION_CHECK_GLOBAL = "__progsitySessionCheck";

type EarlySessionAnswer = { status: number; body: unknown };

/**
 * The parser-started check's answer, read the way `fetcher` reads one: our
 * envelope with `success: false` is an `ApiError` carrying the status (a 401 or
 * 403 ends the session), and anything else that is not a success (a gateway's
 * HTML page, a body that is not JSON) is a plain error that proves nothing
 * about the session, so the bootstrap retries it.
 */
export function settleEarlySessionCheck({ status, body }: EarlySessionAnswer): RefreshResponse {
  const envelope = body as { success?: unknown; data?: unknown; error?: { code?: string; message?: string } } | null;
  if (envelope && envelope.success === true && status >= 200 && status < 300) {
    const data = envelope.data as RefreshResponse | undefined;
    if (data && typeof data.accessToken === "string") return data;
  }
  if (envelope && envelope.success === false) {
    throw new ApiError(envelope.error?.message ?? `Request failed: ${status}`, status, envelope.error?.code);
  }
  throw new Error(`Session check failed: ${status}`);
}

function takeEarlySessionCheck(): Promise<RefreshResponse> | null {
  if (typeof window === "undefined") return null;
  const slot = window as unknown as Record<string, Promise<EarlySessionAnswer> | undefined>;
  const pending = slot[EARLY_SESSION_CHECK_GLOBAL];
  if (!pending) return null;
  // Single use: the refresh token it spent is gone, so nothing may read it twice.
  slot[EARLY_SESSION_CHECK_GLOBAL] = undefined;
  return pending.then(settleEarlySessionCheck);
}

const coordinatedAuthRefresh = createRefreshCoordinator(async (): Promise<RefreshResponse> => {
  const res = await fetcher<RefreshResponse>(REFRESH_PATH, { method: "POST" });
  if (!res.success) throw new Error(res.error?.message);
  return res.data;
});

let earlyRefresh: Promise<RefreshResponse> | null = null;

export function authRefresh(): Promise<RefreshResponse> {
  // Callers that arrive while the parser-started check is still out share it,
  // exactly as the coordinator shares one of its own.
  if (earlyRefresh) return earlyRefresh;
  const early = takeEarlySessionCheck();
  if (early) {
    earlyRefresh = early.finally(() => {
      earlyRefresh = null;
    });
    return earlyRefresh;
  }
  return coordinatedAuthRefresh();
}

export async function authLogout(token: string | null): Promise<void> {
  await fetcher("/v1/auth/logout", {
    method: "POST",
    body: JSON.stringify({}),
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function fetchMe(token: string): Promise<{ user: AuthUser }> {
  const res = await fetcher<{ user: AuthUser }>("/v1/me", {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.success) throw new Error(res.error?.message);
  return res.data;
}

export async function authEndImpersonation(token: string): Promise<{ adminPanelUrl: string }> {
  const res = await fetcher<{ ok: boolean; adminPanelUrl: string }>("/v1/auth/impersonation/end", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({}),
  });
  if (!res.success) throw new Error(res.error?.message);
  return { adminPanelUrl: res.data.adminPanelUrl };
}
